import type { EnrichedVehicleDetails } from '@web/vin-sessions/types';

import { colors } from '@packages/core/styles';
import { Typography } from '@packages/ui/shared';
import placeholderCarImage from '@web/vin-sessions/assets/placeholder_car_image.svg';
import { useCallback, useState } from 'react';
import { createUseStyles } from 'react-jss';

import { ssm5TextStyles } from '../ssm5.styles';

/** Props for {@link Ssm5VehicleCard}. */
export type Ssm5VehicleCardProps = {
    /** The vehicle to present. */
    vehicle: EnrichedVehicleDetails;
};

const useStyles = createUseStyles({
    card: {
        alignItems: 'center',
        border: `1px solid ${colors.grayThree}`,
        borderRadius: 4,
        display: 'flex',
        gap: 8,
        padding: '11px 15px',
    },
    image: {
        height: 24,
        objectFit: 'contain',
        width: 55,
    },
});

/**
 * The vehicle identity row: image, VIN, and model year with model.
 *
 * @param props - {@link Ssm5VehicleCardProps}
 * @returns the card
 */
export function Ssm5VehicleCard({ vehicle }: Ssm5VehicleCardProps) {
    const classes = useStyles();
    const [hasImageFailed, setHasImageFailed] = useState(false);
    const imageSrc = hasImageFailed || !vehicle.vehicleImageUrl ? placeholderCarImage : vehicle.vehicleImageUrl;

    const markImageFailed = useCallback(() => setHasImageFailed(true), []);

    return (
        <div className={classes.card}>
            <img alt={vehicle.vinRecord.model} className={classes.image} onError={markImageFailed} src={imageSrc} />
            <Typography color="blueLink" style={ssm5TextStyles.vinLink} variant="default">
                {vehicle.vinRecord.vin}
            </Typography>
            <Typography style={ssm5TextStyles.body} variant="default">
                {`${vehicle.vinRecord.modelYear} ${vehicle.vinRecord.model}`}
            </Typography>
        </div>
    );
}
